import Modifier from 'ember-modifier';
import tippy from 'tippy.js';
import { inject as service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import normalizeIdentifier from 'ember-stereo/-private/utils/normalize-identifier';
import { task, timeout } from 'ember-concurrency';
import blockedAutoplayMessage from '../utils/blocked-autoplay-message';

export default class HintOnAudioBlocked extends Modifier {
  @service stereo;

  identifier;
  element;
  prompt;

  constructor() {
    super(...arguments);
    // Bind once and reuse the same refs for on/off — re-binding (or invoking)
    // in unregister would never match, leaking the listener.
    this._onAudioBlocked = this.onAudioBlocked.bind(this);
    this._onAudioPlayed = this.onAudioPlayed.bind(this);
    registerDestructor(this, this.teardown.bind(this));
    this.stereo.on('audio-blocked', this._onAudioBlocked);
    this.stereo.on('audio-played', this._onAudioPlayed);
    this.stereo.on('audio-dismissed', this._onAudioPlayed);
  }

  // The identifier re-tokenizes as a feed settles, which must not take a showing prompt with it.
  modify(element, [identifier]) {
    this.element = element;
    this.identifier = normalizeIdentifier(identifier);
  }

  onAudioPlayed({ sound }) {
    if (sound.hasUrl(this.identifier)) {
      this.showPrompt.cancelAll();
      this.prompt?.hide();
    }
  }

  // The window layout covers the whole player with the same message.
  get coveredByOverlay() {
    return !!this.element?.closest('.dubbletrack-system.mod-window-layout');
  }

  onAudioBlocked({ sound }) {
    if (sound.hasUrl(this.identifier) && !this.coveredByOverlay) {
      this.showPrompt.perform(sound);
    }
  }

  showPrompt = task({ restartable: true }, async (sound) => {
    this.prompt ??= tippy(this.element, {
      role: 'tooltip',
      theme: 'attention',
      placement: 'bottom',
      maxWidth: '300px',
      hideOnClick: true,
      interactive: false,
      trigger: 'manual',
      allowHTML: true,
      appendTo: () => this.element.closest('.dubbletrack-system') ?? document.body,
    });
    let airing = sound.metadata?.airing;
    this.prompt.setContent(blockedAutoplayMessage({ title: airing?.showTitle ?? airing?.show?.title }));
    this.prompt.show();
    await timeout(8000);
    this.prompt.hide();
  });

  teardown() {
    this.stereo.off('audio-blocked', this._onAudioBlocked);
    this.stereo.off('audio-played', this._onAudioPlayed);
    this.stereo.off('audio-dismissed', this._onAudioPlayed);
    this.prompt?.destroy();
  }
}
